import React from 'react';
import axios from 'axios';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import { Retailer } from '@ordermentum/ui-types';
import { isABN } from '../../validations/abn';

type RetailerRegisterFormValues = {
  tradingName: string;
  legalName: string;
  abn: string;
  deliveryStreetAddress1: string;
  deliveryStreetAddress2: string;
  deliverySuburb: string;
  deliveryState: string;
  deliveryPostcode: string;
  billingStreetAddress1: string;
  billingStreetAddress2: string;
  billingSuburb: string;
  billingState: string;
  billingPostcode: string;
  phone: string;
  email: string;
};

/**
 * Properties passed to the child component of the RetailerRegisterForm
 */
export type RetailerRegisterFormComponentProps = {
  // The full suite of formik properties
  formik: FormikProps<RetailerRegisterFormValues>;
  // Is the billing address the same as the delivery address
  isSameBilling: boolean;
  // Update the isSameBilling field
  setSameBilling: (isSameBilling: boolean) => void;
  // Other error messages not part of form validation (e.g. messages derived
  // from HTTP errors)
  error?: string;
};

export type RetailerRegisterExposedProps = {
  // Any values to fill the form with
  // email is required
  defaultValues: Partial<RetailerRegisterFormValues> &
    Pick<RetailerRegisterFormValues, 'email'>;
  // called when the form begins submission
  onSubmission?: () => void;
  // called when the form has successfully submitted and created a new retailer
  onSuccessfulSubmission?: (retailer: Retailer) => void;
  // called when the form fails to submit
  onFailedSubmission?: () => void;
};

type RetailerRegisterBaseProps = RetailerRegisterExposedProps & {
  children: React.ComponentType<RetailerRegisterFormComponentProps>;
};

export const genSchema = (includeDeliveryAddress = true) => {
  const deliveryAddressSchema = {
    deliveryStreetAddress1: yup
      .string()
      .required('Delivery Address is required'),
    deliveryStreetAddress2: yup.string(),
    deliverySuburb: yup.string().required('Delivery Suburb is required'),
    deliveryState: yup.string().required('Delivery State is required'),
    deliveryPostcode: yup.string().required('Delivery Postcode is required'),
  };

  return yup.object().shape({
    tradingName: yup.string().required('Business Name is required'),
    legalName: yup.string().required('Legal Name is required'),
    abn: yup.string().test('is-abn', 'Must be a valid ABN number', isABN),
    ...(includeDeliveryAddress ? deliveryAddressSchema : {}),
    phone: yup.string().required('Phone is required'),
    isSameBilling: yup.boolean(),
    billingStreetAddress1: yup.string().when('isSameBilling', {
      is: false,
      then: s => s.required('Billing Address is required'),
    }),
    billingStreetAddress2: yup.string(),
    billingSuburb: yup.string().when('isSameBilling', {
      is: false,
      then: s => s.required('Billing Suburb is required'),
    }),
    billingState: yup.string().when('isSameBilling', {
      is: false,
      then: s => s.required('Billing State is required'),
    }),
    billingPostcode: yup.string().when('isSameBilling', {
      is: false,
      then: s => s.required('Billing Postcode is required'),
    }),
  });
};

/**
 * This behaves similarly to the <Formik /> component, while handling certain
 * automatic behaviour. Pass a component (not a node) as the child and use that
 * to build the actual form fields. This component uses Formik to handle the
 * form logic, validation and submission.
 *
 * @example
 *
 *   <RetailerRegisterForm
 *     defaultValues={{ email: 'foo@example.com' }}
 *     onSuccessfulSubmission={() => console.log('We did it! New Retailer!')}
 *  >
 *    ({ isSameBilling, setSameBilling, error, formik }) => (
 *      <form onSubmit={formik.handleSubmit}>
 *        // ...
 *        // ...
 *        // ...
 *      </form>
 *    )
 *  </RetailerRegisterForm>
 */
export const RetailerRegisterForm = (props: RetailerRegisterBaseProps) => {
  const {
    children: FormComponent,
    onSuccessfulSubmission,
    onFailedSubmission,
    onSubmission,
    defaultValues,
  } = props;

  // Whether the billing address is the same as the delivery address
  const [isSameBilling, setSameBilling] = React.useState<boolean>(true);

  const [error, setError] = React.useState<string | undefined>();

  return (
    <Formik
      initialValues={{
        tradingName: '',
        legalName: '',
        abn: '',
        deliveryStreetAddress1: '',
        deliveryStreetAddress2: '',
        deliverySuburb: '',
        deliveryState: '',
        deliveryPostcode: '',
        billingStreetAddress1: '',
        billingStreetAddress2: '',
        billingSuburb: '',
        billingState: '',
        billingPostcode: '',
        phone: '',
        isSameBilling: true,
        ...defaultValues,
      }}
      validationSchema={genSchema()}
      validateOnBlur
      validateOnChange={false}
      onSubmit={async (values: RetailerRegisterFormValues) => {
        if (onSubmission) {
          onSubmission();
        }
        const billingAddress = isSameBilling
          ? {
              street1: values.deliveryStreetAddress1,
              street2: values.deliveryStreetAddress2,
              suburb: values.deliverySuburb,
              state: values.deliveryState,
              postcode: values.deliveryPostcode,
              country: 'Australia',
              addressableType: 'retailer',
              type: 'billing',
            }
          : {
              street1: values.billingStreetAddress1,
              street2: values.billingStreetAddress2,
              suburb: values.billingSuburb,
              state: values.billingState,
              postcode: values.billingPostcode,
              country: 'Australia',
              addressableType: 'retailer',
              type: 'billing',
            };
        try {
          const { data } = await axios.post('/v1/venues/signup', {
            tradingName: values.tradingName,
            legalName: values.legalName,
            abn: values.abn,
            phone: values.phone,
            email: values.email,
            address: {
              street1: values.deliveryStreetAddress1,
              street2: values.deliveryStreetAddress2,
              suburb: values.deliverySuburb,
              state: values.deliveryState,
              postcode: values.deliveryPostcode,
              country: 'Australia',
              addressableType: 'retailer',
              type: 'delivery',
            },
            billingAddress,
          });
          if (onSuccessfulSubmission) {
            onSuccessfulSubmission(data.retailer);
          }
        } catch (err) {
          setError('Error creating the venue');
          if (onFailedSubmission) {
            onFailedSubmission();
          }
        }
      }}
    >
      {formik => (
        <FormComponent
          formik={formik}
          error={error}
          isSameBilling={isSameBilling}
          setSameBilling={setSameBilling}
        />
      )}
    </Formik>
  );
};
